import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "../../rails/activestorage"

// Drives the "agradecimento exclusivo" sender flow: a bottom-sheet modal that
// lets the creator either record a video with the camera or pick a file, preview
// it, and submit it. The media is uploaded straight to storage via ActiveStorage
// Direct Upload (real progress, no re-upload through the Rails request); the form
// then submits only the signed blob id. The native multipart form remains a
// graceful fallback when JS is unavailable.
export default class extends Controller {
  static targets = [
    "overlay", "form", "input", "stepUpload", "stepCamera",
    "uploadTitle", "uploadSub", "preview", "submit", "footer",
    "progress", "progressBar", "progressPercent", "progressLabel",
    "camera", "recBadge", "recTime", "recButton", "recLabel"
  ]

  connect() {
    this.pendingFile = null
    this.stream = null
    this.recorder = null
    this.chunks = []
    this.seconds = 0
    this.timer = null
    this.recording = false
    this.maxImage = parseInt(this.data.get("maxImage"), 10) || 0
    this.maxVideo = parseInt(this.data.get("maxVideo"), 10) || 0
    this.uploadingLabel = this.hasProgressLabelTarget ? this.progressLabelTarget.textContent : ""
  }

  disconnect() {
    this.stopCamera()
    this.releasePreview()
  }

  /* ── Modal ── */
  open(event) {
    const mode = event?.params?.mode || "upload"
    this.resetUpload()
    this.overlayTarget.classList.add("is-open")
    document.body.style.overflow = "hidden"
    this.goStep(mode === "record" ? "camera" : "upload")
  }

  closeBackdrop(event) {
    if (event.target !== this.overlayTarget) return
    this.close()
  }

  close() {
    this.stopCamera()
    this.stopRecording(false)
    this.overlayTarget.classList.remove("is-open")
    document.body.style.overflow = ""
    setTimeout(() => this.showStep("upload"), 300)
  }

  /* ── Steps ── */
  goStep(name) {
    this.showStep(name)
    if (name === "camera") {
      this.startCamera()
    } else {
      this.stopCamera()
      this.stopRecording(false)
    }
  }

  showStep(name) {
    this.stepUploadTarget.classList.toggle("hidden", name !== "upload")
    this.stepCameraTarget.classList.toggle("hidden", name !== "camera")
  }

  backToUpload() {
    this.goStep("upload")
  }

  /* ── Camera ── */
  async startCamera() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", aspectRatio: { ideal: 9 / 16 } },
        audio: true
      })
      this.cameraTarget.srcObject = this.stream
    } catch (_e) {
      window.alert(this.data.get("cameraError") || "Não foi possível acessar a câmera.")
      this.goStep("upload")
    }
  }

  stopCamera() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop())
      this.stream = null
    }
    if (this.hasCameraTarget) this.cameraTarget.srcObject = null
  }

  toggleRecording() {
    this.recording ? this.stopRecording(true) : this.startRecording()
  }

  startRecording() {
    if (!this.stream) return
    this.chunks = []
    this.seconds = 0
    this.recording = true

    // Browsers disagree on the recording container: Android Chrome does webm,
    // iOS Safari only does mp4. Let the browser pick from what it supports and
    // read back what it actually produced — never assume a format.
    const mime = this.supportedMimeType()
    this.recorder = mime ? new MediaRecorder(this.stream, { mimeType: mime }) : new MediaRecorder(this.stream)
    this.recorder.ondataavailable = (e) => { if (e.data.size > 0) this.chunks.push(e.data) }
    this.recorder.onstop = () => {
      const recorded = this.recorder.mimeType || this.chunks[0]?.type || "video/mp4"
      const type = recorded.split(";")[0]
      const blob = new Blob(this.chunks, { type })
      const file = new File([blob], `agradecimento.${this.extensionFor(type)}`, { type })
      this.adoptFile(file)
      this.goStep("upload")
    }
    this.recorder.start(100)

    this.recBadgeTarget.classList.add("is-shown")
    this.timer = setInterval(() => {
      this.seconds += 1
      const m = Math.floor(this.seconds / 60)
      const s = String(this.seconds % 60).padStart(2, "0")
      this.recTimeTarget.textContent = `${m}:${s}`
      if (this.seconds >= 60) this.stopRecording(true)
    }, 1000)

    this.recButtonTarget.classList.add("is-recording")
    this.recLabelTarget.textContent = this.data.get("stopLabel") || "Parar gravação"
  }

  stopRecording(save) {
    if (!this.recording && !save) return
    this.recording = false
    clearInterval(this.timer)
    this.recBadgeTarget.classList.remove("is-shown")
    this.recTimeTarget.textContent = "0:00"
    this.recButtonTarget.classList.remove("is-recording")
    this.recLabelTarget.textContent = this.data.get("startLabel") || "Iniciar gravação"
    if (this.recorder && this.recorder.state !== "inactive") this.recorder.stop()
  }

  supportedMimeType() {
    if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return ""
    const candidates = [
      "video/mp4",
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm"
    ]
    return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || ""
  }

  extensionFor(mimeType) {
    return { "video/mp4": "mp4", "video/webm": "webm", "video/quicktime": "mov", "video/x-matroska": "mkv" }[mimeType] || "webm"
  }

  /* ── File handling ── */
  selectFile(event) {
    const file = event.target.files?.[0]
    if (file) this.adoptFile(file)
  }

  // Quick upload from the pending card's own input — adopts the file then opens
  // the modal already showing the preview.
  selectFileAndOpen(event) {
    const file = event.target.files?.[0]
    if (!file) return
    this.resetUpload()
    this.adoptFile(file)
    this.overlayTarget.classList.add("is-open")
    document.body.style.overflow = "hidden"
    this.showStep("upload")
  }

  adoptFile(file) {
    if (this.tooBig(file)) {
      this.inputTarget.value = ""
      window.alert(this.tooBigMessage(file))
      return
    }
    this.pendingFile = file
    const transfer = new DataTransfer()
    transfer.items.add(file)
    this.inputTarget.files = transfer.files
    this.showPreview(file)
  }

  // Reject oversized files before the (potentially long) upload even starts; the
  // limits mirror the server-side validation in GimmoGift::Thankable.
  tooBig(file) {
    const max = (file.type || "").startsWith("video/") ? this.maxVideo : this.maxImage
    return max > 0 && file.size > max
  }

  tooBigMessage(file) {
    return (file.type || "").startsWith("video/")
      ? (this.data.get("tooBigVideo") || "O vídeo é muito grande.")
      : (this.data.get("tooBigImage") || "A imagem é muito grande.")
  }

  showPreview(file) {
    this.uploadTitleTarget.textContent = file.name || this.data.get("selectedLabel") || "Arquivo selecionado"
    const kb = Math.round(file.size / 1024)
    this.uploadSubTarget.textContent = kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`

    this.releasePreview()
    this.objectUrl = URL.createObjectURL(file)
    const type = file.type || ""
    this.previewTarget.innerHTML = ""

    if (type.startsWith("image/")) {
      const img = document.createElement("img")
      img.src = this.objectUrl
      this.previewTarget.appendChild(img)
      this.previewTarget.classList.add("is-shown")
    } else if (type.startsWith("video/")) {
      const video = document.createElement("video")
      video.src = this.objectUrl
      video.controls = true
      video.muted = true
      this.previewTarget.appendChild(video)
      this.previewTarget.classList.add("is-shown")
    }
  }

  // Take over the submit so the upload streams straight to storage with real
  // progress instead of through the Rails request.
  submit(event) {
    event.preventDefault()
    const file = this.pendingFile || (this.inputTarget.files && this.inputTarget.files[0])
    if (!file) {
      window.alert(this.data.get("missingFile") || "Selecione um arquivo primeiro.")
      return
    }
    this.upload(file)
  }

  upload(file) {
    const url = this.inputTarget.dataset.directUploadUrl
    if (!url) return this.uploadMultipart()

    this.startProgress()
    new DirectUpload(file, url, this).create((error, blob) => {
      if (error) {
        this.failUpload()
      } else {
        this.submitSignedBlob(blob.signed_id)
      }
    })
  }

  // ActiveStorage DirectUpload delegate — reports the real browser→storage upload.
  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) this.setProgress((event.loaded / event.total) * 100)
    })
  }

  // The blob is already in storage; submit just its signed id so the server links
  // it without re-uploading the bytes.
  submitSignedBlob(signedId) {
    this.finishProgress()
    const form = this.formTarget
    const hidden = document.createElement("input")
    hidden.type = "hidden"
    hidden.name = this.inputTarget.name
    hidden.value = signedId
    form.appendChild(hidden)
    this.inputTarget.disabled = true
    this.postForm(form)
  }

  // Fallback when Direct Upload isn't available: send the file through the request.
  uploadMultipart() {
    this.startProgress()
    const form = this.formTarget
    const xhr = new XMLHttpRequest()
    xhr.open("POST", form.action)
    xhr.setRequestHeader("Accept", "application/json")
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) this.setProgress((event.loaded / event.total) * 100)
    })
    xhr.upload.addEventListener("load", () => this.finishProgress())
    xhr.addEventListener("load", () => this.handleResponse(xhr, form))
    xhr.addEventListener("error", () => this.failUpload())
    xhr.addEventListener("abort", () => this.failUpload())
    xhr.send(new FormData(form))
  }

  postForm(form) {
    const xhr = new XMLHttpRequest()
    xhr.open("POST", form.action)
    xhr.setRequestHeader("Accept", "application/json")
    xhr.addEventListener("load", () => this.handleResponse(xhr, form))
    xhr.addEventListener("error", () => this.failUpload())
    xhr.send(new FormData(form))
  }

  handleResponse(xhr, form) {
    let payload = {}
    try { payload = JSON.parse(xhr.responseText) } catch (_e) { /* non-JSON response */ }
    if (xhr.status >= 200 && xhr.status < 300) {
      window.location.href = payload.redirect || form.action
    } else {
      this.failUpload(payload.error)
    }
  }

  startProgress() {
    this.submitTarget.disabled = true
    this.setProgress(0)
    this.progressTarget.classList.remove("is-finishing")
    if (this.hasProgressLabelTarget) this.progressLabelTarget.textContent = this.uploadingLabel
    if (this.hasFooterTarget) this.footerTarget.classList.add("hidden")
    this.progressTarget.classList.add("is-shown")
  }

  setProgress(percent) {
    const value = Math.max(0, Math.min(100, Math.round(percent)))
    this.progressBarTarget.style.width = `${value}%`
    this.progressPercentTarget.textContent = `${value}%`
  }

  finishProgress() {
    this.setProgress(100)
    this.progressPercentTarget.textContent = ""
    if (this.hasProgressLabelTarget) this.progressLabelTarget.textContent = this.data.get("finishing") || "Finalizando…"
    this.progressTarget.classList.add("is-finishing")
  }

  failUpload(message) {
    this.progressTarget.classList.remove("is-shown", "is-finishing")
    if (this.hasFooterTarget) this.footerTarget.classList.remove("hidden")
    this.submitTarget.disabled = false
    window.alert(message || this.data.get("uploadError") || "Não foi possível enviar. Tente novamente.")
  }

  resetUpload() {
    this.pendingFile = null
    this.inputTarget.value = ""
    this.uploadTitleTarget.textContent = this.data.get("selectLabel") || "Selecionar arquivo"
    this.uploadSubTarget.textContent = this.data.get("selectHint") || "Clique para escolher da galeria"
    this.releasePreview()
    this.previewTarget.innerHTML = ""
    this.previewTarget.classList.remove("is-shown")
    if (this.hasSubmitTarget) this.submitTarget.disabled = false
    if (this.hasProgressTarget) this.progressTarget.classList.remove("is-shown", "is-finishing")
    if (this.hasFooterTarget) this.footerTarget.classList.remove("hidden")
  }

  releasePreview() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl)
      this.objectUrl = null
    }
  }
}
